import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import OnyxApi from "../../components/OnyxApi";
import CommonIssuesList from "../../components/AffordableOption/CommonIssuesList";
import RateDetail from "../../components/AffordableOption/RateDetail";
import TechRateDetail from "../../components/AffordableOption/TechRateDetail";
import MonthlyPaymentEstimator from "../../components/AffordableOption/MonthlyPaymentEstimator";
import ComponentSearchModal from "../../components/AffordableOption/ComponentSearchModal";

function Recommendations({
  aiResults,
  chosenComponents,
  currentComponentNames,
  componentsOrdered,
  deductible,
  handleRestart,
  loadAiRecommendations,
  mileage,
  products,
  rates,
  selectedProgram,
  setAiResults,
  setChosenComponents,
  setCurrentComponentNames,
  setDeductible,
  setSelectedProgram,
  setTerm,
  setTermMileage,
  showFullAndWrapPrograms,
  term,
  termMileage,
  vehicle,
}) {
  const navigate = useNavigate();
  const { user, logout } = useContext(UserContext);
  const [errors, setErrors] = useState([]);
  const [showWrap, setShowWrap] = useState(false);
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [rateTotals, setRateTotals] = useState({});
  const userToken = user?.usertoken;

  const handleRateTotalChange = useCallback((rateId, total) => {
    setRateTotals((prev) =>
      prev[rateId] === total ? prev : { ...prev, [rateId]: total },
    );
  }, []);

  const handleLogout = () => {
    handleRestart();
    logout();
    navigate("/");
  };

  const handleRestartProcess = () => {
    handleRestart();
    navigate("/vehicle-submission", { replace: true });
  };

  const loadAiResults = useCallback(async () => {
    setIsLoadingAi(true);
    setErrors([]);
    try {
      const response = await OnyxApi(
        "promptsExecute",
        {
          slug: "affordable-option-ai-issues-costs",
          year: vehicle.year,
          make: vehicle.make,
          model: vehicle.model,
        },
        userToken,
      );

      // Check if the response indicates an error from the AI provider
      if (response?.status === "error") {
        setErrors([
          response.message ||
            "An error occurred while generating AI recommendations",
        ]);
        return;
      }

      setAiResults(response);
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const errors = error.response.data.errors;
        const flattenedErrors = Object.values(errors).flat();
        setErrors(flattenedErrors);
      } else {
        setErrors([error.message]);
      }
    } finally {
      setIsLoadingAi(false);
    }
  }, [vehicle, userToken, setAiResults]);

  const filterRates = useMemo(() => {
    const ratesMap = {
      Wrap: rates?.filter((rate) => rate.name.includes("Wrap")),
      Tech: rates?.filter((rate) => rate.name.includes("Tech")),
      Per: rates?.filter((rate) => rate.name.includes("Per")),
      Default: rates?.filter(
        (rate) =>
          rate.name.includes("Affordable") &&
          !rate.name.includes("Tech") &&
          !rate.name.includes("Wrap") &&
          !rate.name.includes("Per"),
      ),
    };
    return (keyword) => ratesMap[keyword];
  }, [rates]);

  const affordableOptionWrapRates = useMemo(
    () => filterRates("Wrap"),
    [filterRates],
  );
  const affordableOptionTechRates = useMemo(
    () => filterRates("Tech"),
    [filterRates],
  );
  const affordableOptionPerComponentRates = useMemo(
    () => filterRates("Per"),
    [filterRates],
  );
  const affordableOptionRates = useMemo(
    () => filterRates("Default"),
    [filterRates],
  );

  const [currentAffordableOptionRate, setCurrentAffordableOptionRate] =
    useState(null);
  const [currentAffordableTechRate, setCurrentAffordableTechRate] =
    useState(null);
  const [currentAffordableWrapRate, setCurrentAffordableWrapRate] =
    useState(null);
  const [
    currentAffordablePerComponentRate,
    setCurrentAffordablePerComponentRate,
  ] = useState(null);

  const allFilteredRates = useMemo(
    () => [
      ...(affordableOptionRates ?? []),
      ...(affordableOptionWrapRates ?? []),
      ...(affordableOptionTechRates ?? []),
      ...(affordableOptionPerComponentRates ?? []),
    ],
    [affordableOptionRates, affordableOptionWrapRates, affordableOptionTechRates, affordableOptionPerComponentRates],
  );

  const uniqueDeductibles = useMemo(
    () =>
      Array.from(new Set(allFilteredRates.map((rate) => rate.deductible))).sort(
        (a, b) => a - b,
      ),
    [allFilteredRates],
  );
  const uniqueMileages = useMemo(
    () =>
      Array.from(
        new Set(allFilteredRates.map((rate) => rate.term_mileage).sort((a, b) => a - b)),
      ),
    [allFilteredRates],
  );
  const uniqueTerms = useMemo(
    () =>
      Array.from(new Set(allFilteredRates.map((rate) => rate.term))).sort(
        (a, b) => a - b,
      ),
    [allFilteredRates],
  );

  useEffect(() => {
    if (!userToken) {
      navigate("/", { replace: true });
      return;
    }
    if (!vehicle) {
      navigate("/vehicle-submission", { replace: true });
      return;
    }
    if (loadAiRecommendations) {
      loadAiResults();
    }
  }, [vehicle, userToken, navigate, loadAiRecommendations, loadAiResults]);

  const handleNext = () => {
    setCurrentComponentNames(
      currentAffordablePerComponentRate?.products
        .filter((product) => chosenComponents.includes(product.id))
        .map((product) => product.name),
    );
    navigate("/disclosure", { replace: true });
  };

  const setCurrentRates = useCallback(
    (newTerm, newDeductible) => {
      newTerm = parseInt(newTerm);
      newDeductible = parseInt(newDeductible);
      setCurrentAffordableOptionRate(
        affordableOptionRates?.find(
          (rate) => rate.term === newTerm && rate.deductible === newDeductible,
        ),
      );
      setCurrentAffordableTechRate(
        affordableOptionTechRates?.find(
          (rate) => rate.term === newTerm && rate.deductible === newDeductible,
        ),
      );
      setCurrentAffordableWrapRate(
        affordableOptionWrapRates?.find(
          (rate) => rate.term === newTerm && rate.deductible === newDeductible,
        ),
      );
      setCurrentAffordablePerComponentRate(
        affordableOptionPerComponentRates?.find(
          (rate) => rate.term === newTerm && rate.deductible === newDeductible,
        ),
      );
    },
    [
      affordableOptionRates,
      affordableOptionTechRates,
      affordableOptionWrapRates,
      affordableOptionPerComponentRates,
      setCurrentAffordableOptionRate,
      setCurrentAffordableTechRate,
      setCurrentAffordableWrapRate,
      setCurrentAffordablePerComponentRate,
    ],
  );

  const handleTermChange = (e) => {
    const newTerm = e.target.value;
    setCurrentComponentNames(
      currentAffordablePerComponentRate?.products
        .filter((product) => chosenComponents.includes(product.id))
        .map((product) => product.name),
    );
    const selectedTermIndex = uniqueTerms.indexOf(parseInt(newTerm));
    setTerm(newTerm);
    setTermMileage(uniqueMileages[selectedTermIndex]);
    setCurrentRates(newTerm, deductible);
  };

  const handleDeductibleChange = (e) => {
    const newDeductible = e.target.value;
    setCurrentComponentNames(
      currentAffordablePerComponentRate?.products
        .filter((product) => chosenComponents.includes(product.id))
        .map((product) => product.name),
    );
    setDeductible(newDeductible);
    setCurrentRates(term, newDeductible);
  };

  useEffect(() => {
    if (currentAffordablePerComponentRate) {
      const filteredProductIds = currentAffordablePerComponentRate.products
        .filter((product) => currentComponentNames.includes(product.name))
        .map((product) => product.id);
      setChosenComponents(filteredProductIds);
    }
  }, [
    currentAffordablePerComponentRate,
    currentComponentNames,
    setChosenComponents,
  ]);

  useEffect(() => {
    if (!term) {
      setTerm(uniqueTerms[uniqueTerms.length - 1]);
    }
    if (deductible == null) {
      setDeductible(uniqueDeductibles[0]);
    }
    if (termMileage == null) {
      setTermMileage(uniqueMileages[uniqueTerms.length - 1]);
    }
    setCurrentRates(term, deductible);
  }, [
    term,
    deductible,
    termMileage,
    uniqueTerms,
    uniqueDeductibles,
    uniqueMileages,
    setCurrentRates,
    setDeductible,
    setTerm,
    setTermMileage,
  ]);

  if (!vehicle) {
    return null;
  }

  return (
    <div className="recommendations-page vh-100">
      <div className="container h-100 position-relative z-2">
        <div className="d-flex justify-content-end pt-1">
          <button
            className="btn btn-secondary me-2"
            onClick={handleRestartProcess}
            title="Restart the vehicle submission process"
          >
            Restart
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={handleLogout}
            title="Logout of the application"
          >
            Logout
          </button>
        </div>
        <div className="row pt-2">
          <div className="col-4"></div>
          <div className="col-4">
            <h4 className="text-center">
              <strong>
                {vehicle.year} {vehicle.make} {vehicle.model}
              </strong>
              <br />
              {mileage} Miles
            </h4>
          </div>
          <div className="col-4">
            <div className="row">
              <div className="col-4">
                <label htmlFor="deductible">Deductible</label>
                <select
                  className="form-select"
                  id="deductible"
                  name="deductible"
                  onChange={handleDeductibleChange}
                  value={deductible}
                >
                  {uniqueDeductibles.map((deductible) => (
                    <option key={deductible} value={deductible}>
                      {deductible}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col text-center">
                <label htmlFor="term" className="text-center">
                  Term <span>{term}</span> mo./{" "}
                  {parseInt(mileage) + parseInt(termMileage)} Miles
                </label>
                <input
                  className="form-range"
                  type="range"
                  id="term"
                  name="term"
                  min={uniqueTerms[0]}
                  max={uniqueTerms[uniqueTerms.length - 1]}
                  onChange={handleTermChange}
                  step="12"
                  value={term}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="row h-75">
          <div className="col-lg-4 col-md-6 mb-4 pe-5 h-100">
            <div className="d-flex flex-column h-100">
              <h5>
                <strong>AI Validation Results</strong>
              </h5>
              {errors &&
                Array.isArray(errors) &&
                errors.length > 0 &&
                errors.map((error, index) => (
                  <div className="errors">
                    <div
                      className="alert alert-danger"
                      role="alert"
                      key={index}
                    >
                      {error}
                    </div>
                  </div>
                ))}
              <div className="row h-100">
                {aiResults?.result.commonIssuesAndExpenses.length > 0 ? (
                  <CommonIssuesList
                    issues={aiResults.result.commonIssuesAndExpenses}
                  />
                ) : !loadAiRecommendations && !aiResults ? (
                  <div className="text-center">
                    <p className="mb-3">
                      AI recommendations are not loaded. Click the button below
                      to load them.
                    </p>
                    <button
                      className="btn btn-primary"
                      onClick={loadAiResults}
                      disabled={isLoadingAi}
                    >
                      {isLoadingAi ? "Loading..." : "Load AI Recommendations"}
                    </button>
                  </div>
                ) : (
                  <p>
                    Generating AI Results
                    <span className="blinking-cursor">|</span>
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-8 mb-4">
            <div className="row">
              {showFullAndWrapPrograms ? (
                <div className="col-md-6 mb-4">
                  <h5
                    className={`d-flex align-items-end ${affordableOptionWrapRates?.length === 0 ? "mt-2" : ""}`}
                  >
                    Affordable Option
                    {affordableOptionWrapRates?.length > 0 ? (
                      <div className="col text-end">
                        <label
                          className="form-check-label"
                          htmlFor="wrapSwitchCheck"
                        >
                          Wrap
                        </label>
                        <br />
                        <div className="form-check form-check-reverse form-switch fs-4">
                          <input
                            className="form-check-input form-check-primary"
                            type="checkbox"
                            role="switch"
                            id="wrapSwitchCheck"
                            onClick={() => setShowWrap(!showWrap)}
                          />
                        </div>
                      </div>
                    ) : null}
                  </h5>
                  <RateDetail
                    componentsOrdered={componentsOrdered}
                    deductible={deductible}
                    onTotalChange={handleRateTotalChange}
                    products={products}
                    rate={currentAffordableOptionRate}
                    selectedProgram={selectedProgram}
                    setSelectedProgram={setSelectedProgram}
                    shouldShow={!showWrap}
                    term={term}
                  />
                  {affordableOptionWrapRates?.length > 0 ? (
                    <RateDetail
                      componentsOrdered={componentsOrdered}
                      deductible={deductible}
                      onTotalChange={handleRateTotalChange}
                      products={products}
                      rate={currentAffordableWrapRate}
                      selectedProgram={selectedProgram}
                      setSelectedProgram={setSelectedProgram}
                      shouldShow={showWrap}
                      term={term}
                    />
                  ) : null}
                </div>
              ) : null}
              <div className="col-md-6 mb-4">
                <h5 className="mt-2">
                  {affordableOptionWrapRates?.length > 0 ? <br /> : null}
                  Per Component
                  {products && <ComponentSearchModal products={products} componentsOrdered={componentsOrdered} />}
                </h5>
                <RateDetail
                  canShowSelectionAlert={
                    affordableOptionRates?.length > 0 ||
                    affordableOptionWrapRates?.length > 0
                  }
                  chosenComponents={chosenComponents}
                  componentsOrdered={componentsOrdered}
                  deductible={deductible}
                  products={products}
                  rate={currentAffordablePerComponentRate}
                  selectedProgram={selectedProgram}
                  setChosenComponents={setChosenComponents}
                  setSelectedProgram={setSelectedProgram}
                  term={term}
                />
              </div>
            </div>
            {affordableOptionTechRates?.length > 0 ? (
              <div className="row justify-content-center">
                <div className="col-md-6">
                  <TechRateDetail
                    deductible={deductible}
                    onTotalChange={handleRateTotalChange}
                    products={products}
                    rate={currentAffordableTechRate}
                    selectedProgram={selectedProgram}
                    setSelectedProgram={setSelectedProgram}
                    term={term}
                  />
                </div>
              </div>
            ) : null}
            <div className="row">
              <div className="col-md-9">
                <MonthlyPaymentEstimator
                  basePrice={currentAffordableOptionRate}
                  selectedProgram={selectedProgram}
                  selectedProgramTotal={
                    selectedProgram ? rateTotals[selectedProgram.id] : undefined
                  }
                  perComponentRate={currentAffordablePerComponentRate}
                  chosenComponents={chosenComponents}
                />
              </div>
              <div className="col-md-3 text-end">
                <button
                  className={`btn btn-lg ${
                    selectedProgram ? "btn-success text-white" : "btn-primary"
                  }`}
                  onClick={handleNext}
                >
                  Next &gt;
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Recommendations;
